import { useState, useEffect } from 'react';
import { MarketplaceAuth } from './components/MarketplaceAuth';
import { MarketplaceRegister } from './components/MarketplaceRegister';
import { AuditDetail } from './components/AuditDetail';
import { Home } from './components/Home';
import { MARKETPLACE_URL } from './config';

/**
 * App - Main application component for the read-only audit console.
 *
 * Authentication is handled via AWS Marketplace JWT tokens.
 *
 * Routes:
 * - /register: Post-Marketplace registration form
 * - /error: Error page for failed registrations
 * - /audits/:traceId: Audit run detail (read-only)
 * - /: Unified home — console hero, New Audit form and review queue
 */
function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Simple client-side routing
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  function navigate(path) {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  }

  // Handle /register route - post-Marketplace registration form
  if (currentPath === '/register') {
    return (
      <MarketplaceRegister
        onComplete={() => navigate('/')}
      />
    );
  }

  // Handle /error route - registration errors
  if (currentPath === '/error') {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason') || 'unknown';

    const errorMessages = {
      invalid_token: 'The registration token is invalid or has expired.',
      registration_failed: 'Error processing registration. Please try again.',
      unknown: 'An unexpected error occurred.',
    };

    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Error</h2>
          <div className="auth-error" style={{ marginTop: '1rem' }}>
            {errorMessages[reason] || errorMessages.unknown}
          </div>
          <a
            href={MARKETPLACE_URL}
            className="btn-primary"
            style={{ marginTop: '1.5rem', textDecoration: 'none', display: 'block', textAlign: 'center' }}
          >
            Back to AWS Marketplace
          </a>
          <button
            className="btn-secondary"
            style={{ marginTop: '0.75rem', width: '100%' }}
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Detail route: /audits/<traceId>
  const detailMatch = currentPath.match(/^\/audits\/([^/]+)$/);

  // Main application with Marketplace auth
  return (
    <MarketplaceAuth>
      {({ user, subscription, signOut }) => (
        <div className="app">
          <header className="app-header">
            <h1>Audit Console</h1>
            <div className="user-info">
              <span>{user?.email || user?.customerId || 'User'}</span>
              {subscription?.status === 'active' && (
                <span className="subscription-badge">Active</span>
              )}
              <button onClick={signOut} className="btn-logout">
                Sign Out
              </button>
            </div>
          </header>
          <main>
            {detailMatch ? (
              <AuditDetail
                traceId={decodeURIComponent(detailMatch[1])}
                onBack={() => navigate('/')}
              />
            ) : (
              <Home
                onOpenTrace={(traceId) => navigate(`/audits/${encodeURIComponent(traceId)}`)}
              />
            )}
          </main>
          <footer className="footer">
            powered by <a href="https://surus.dev" target="_blank" rel="noopener noreferrer">SURUS AI Nodes</a>
          </footer>
        </div>
      )}
    </MarketplaceAuth>
  );
}

export default App;
